/**
 * Phase 0.5 Component G — TanStack Query bindings for the /admin/access
 * page. All four admin RPCs (list bundles, list assignments, create,
 * revoke, request break-glass) live behind hooks here so the page +
 * dialogs stay declarative.
 */ import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { authzClient, deliveryClient } from '../../../lib/connect';
const BUNDLES_KEY = 'access:bundles';
const ASSIGNMENTS_KEY = 'access:assignments-for-user';
const SCOPE_NODES_KEY = 'access:scope-nodes';
const TENANT_MEMBERS_KEY = 'access:tenant-members';
/** Catalog of role bundles (global + tenant-scoped). */ // The canonical sensitive-data pack catalog (server-owned, read-only). The
// matrix and role editor expand a bundle's pack keys into field names with it.
export function useListDataPacks() {
    return useQuery({
        queryKey: [
            'admin:data-packs'
        ],
        staleTime: 60 * 60000,
        queryFn: async ()=>{
            const resp = await authzClient.adminListDataPacks({});
            return resp.packs;
        }
    });
}
export function useListBundles(includeGlobal = true, options = {}) {
    const { enabled = true } = options;
    return useQuery({
        queryKey: [
            BUNDLES_KEY,
            includeGlobal
        ],
        enabled,
        staleTime: 5 * 60000,
        queryFn: async ()=>{
            const resp = await authzClient.adminListBundles({
                includeGlobal
            });
            return resp.bundles;
        }
    });
}
/** Assignments for one user — the page's table data source. */ export function useListAssignmentsForUser(userId, options = {}) {
    const { includeInactive = false, enabled = true } = options;
    return useQuery({
        queryKey: [
            ASSIGNMENTS_KEY,
            userId,
            includeInactive
        ],
        enabled: enabled && !!userId,
        queryFn: async ()=>{
            const resp = await authzClient.adminListAssignmentsForUser({
                userId,
                includeInactive
            });
            return resp.assignments;
        }
    });
}
/**
 * Tenant scope nodes matching a display-name/target-id substring — the grant
 * dialog's scope picker. Newest first; empty query returns the latest nodes.
 * The query is debounced so typing doesn't fire one RPC per keystroke, and
 * the previous result set stays on screen while the next one loads.
 */ export function useListScopeNodes(query, options = {}) {
    const { enabled = true } = options;
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    useEffect(()=>{
        const handle = setTimeout(()=>setDebouncedQuery(query), 250);
        return ()=>clearTimeout(handle);
    }, [
        query
    ]);
    return useQuery({
        queryKey: [
            SCOPE_NODES_KEY,
            debouncedQuery
        ],
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            // Member-facing typeahead (D12): adminListScopeNodes is admin-only and
            // 403s a plain owner filing their own delegation request — this
            // dialog is reused by both the admin power tools and the self-service
            // panel, so it must call the member-gated RPC instead.
            const resp = await authzClient.listScopeNodes({
                query: debouncedQuery,
                limit: 20
            });
            return resp.scopeNodes;
        }
    });
}
/**
 * Tenant members matching a display-name substring — the delegate picker's
 * name search (SAD-112 item b): the self-service delegation dialog made a
 * member type the delegate's raw user id, so this backs a searchable name
 * dropdown instead. Same debounce/placeholder-data shape as
 * useListScopeNodes so the two pickers in this dialog behave identically.
 */ export function useSearchTenantMembers(query, options = {}) {
    const { enabled = true } = options;
    const [debouncedQuery, setDebouncedQuery] = useState(query);
    useEffect(()=>{
        const handle = setTimeout(()=>setDebouncedQuery(query), 250);
        return ()=>clearTimeout(handle);
    }, [
        query
    ]);
    return useQuery({
        queryKey: [
            TENANT_MEMBERS_KEY,
            debouncedQuery
        ],
        enabled,
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            const resp = await authzClient.searchTenantMembers({
                query: debouncedQuery,
                limit: 20
            });
            return resp.members;
        }
    });
}
export function useGrantAssignment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await authzClient.adminCreateRoleAssignment({
                userId: args.userId,
                roleBundleKey: args.roleBundleKey,
                scopeNodeId: args.scopeNodeId,
                assignmentType: args.assignmentType ?? 'primary',
                reason: args.reason,
                originalOwnerUserId: args.originalOwnerUserId ?? '',
                effectiveEnd: args.effectiveEnd,
                effectiveStart: args.effectiveStart
            });
            return resp;
        },
        onSuccess: (_resp, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    ASSIGNMENTS_KEY,
                    vars.userId
                ]
            });
            toast.success('Role grant created');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to grant role');
        }
    });
}
/**
 * Mints an authz.delegation header plus its mirrored designee rows
 * (AdminCreateDelegation) — the designee path's actual RPC (doc 03). Replaces
 * adminCreateRoleAssignment for assignmentType === 'designee', which used to
 * mint one standalone row with no header at all.
 */ export function useCreateDelegation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await authzClient.adminCreateDelegation({
                ownerUserId: args.ownerUserId,
                delegateUserId: args.delegateUserId,
                startsAt: args.startsAt,
                endsAt: args.endsAt,
                reason: args.reason,
                roleBundleKey: args.roleBundleKey ?? '',
                scopeNodeId: args.scopeNodeId ?? ''
            });
            return resp;
        },
        onSuccess: (_resp, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    ASSIGNMENTS_KEY,
                    vars.delegateUserId
                ]
            });
            toast.success('Delegation created');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create delegation');
        }
    });
}
export function useRevokeAssignment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await authzClient.adminRevokeRoleAssignment({
                id: args.id,
                reason: args.reason
            });
            return resp;
        },
        onSuccess: (_resp, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    ASSIGNMENTS_KEY,
                    vars.userId
                ]
            });
            toast.success('Role grant revoked');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to revoke');
        }
    });
}
export function useRequestBreakGlass() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await authzClient.adminRequestBreakGlass({
                userId: args.userId,
                roleBundleKey: args.roleBundleKey,
                scopeNodeId: args.scopeNodeId,
                reason: args.reason,
                durationHours: args.durationHours ?? 24
            });
            return resp;
        },
        onSuccess: (_resp, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    ASSIGNMENTS_KEY,
                    vars.userId
                ]
            });
            toast.success('Break-glass grant created');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to request break-glass');
        }
    });
}
/**
 * Create the scope_node that owns an object. A grant needs a scope_node_id;
 * without this the admin had to already know one (the grant dialog's MVP
 * manual entry). Returns the created node so the page can surface its id to
 * paste into a grant.
 */ export function useCreateScopeNode() {
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await authzClient.adminCreateScopeNode({
                kind: args.kind,
                targetObjectType: args.targetObjectType,
                targetObjectId: args.targetObjectId,
                displayName: args.displayName ?? '',
                parentId: args.parentId ?? '',
                inheritsFromParent: args.inheritsFromParent ?? false,
                sensitivity: args.sensitivity ?? 'standard'
            });
            return resp.scopeNode;
        },
        onSuccess: (node)=>{
            toast.success(`Scope node created: ${node?.id ?? ''}`);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create scope node');
        }
    });
}
export { BUNDLES_KEY, ASSIGNMENTS_KEY };
const GRANT_EVENTS_KEY = 'access:grant-events';
const DEFAULT_HISTORY_PAGE_SIZE = 50;
/**
 * Tenant-wide grant ledger, newest first (Wave 3b grant-history surface).
 * self_grant rows are the ones the panel must make loud. Served by the
 * member-grade ListAccessHistory (trio-gated: capability, grantor, admin) so
 * non-admin designated grantors see the same ledger (round 7).
 *
 * Filters + pageToken are pushed to the server (SAD-104) — narrowing happens
 * in pangaea's SQL, not client-side. keepPreviousData means Next/Prev don't
 * flash a loading state while the new page fetches.
 */ export function useListGrantEvents(filters = {}, pageToken = '') {
    const { subjectUserId = '', actorUserId = '', eventKinds = [], createdAfter, createdBefore } = filters;
    return useQuery({
        queryKey: [
            GRANT_EVENTS_KEY,
            subjectUserId,
            actorUserId,
            eventKinds,
            createdAfter?.toJson(),
            createdBefore?.toJson(),
            pageToken
        ],
        placeholderData: keepPreviousData,
        queryFn: async ()=>{
            const resp = await authzClient.listAccessHistory({
                subjectUserId,
                actorUserId,
                eventKinds,
                createdAfter,
                createdBefore,
                pagination: {
                    pageSize: DEFAULT_HISTORY_PAGE_SIZE,
                    pageToken
                }
            });
            return {
                events: resp.events,
                pagination: resp.pagination
            };
        }
    });
}
/**
 * Reviewer-facing role vocabulary for the approve picker (round 7) — the
 * member-grade twin of the admin bundle list. PermissionDenied for
 * non-reviewers; retry off so a denial doesn't hammer.
 */ export function useGrantableRoles(enabled = true) {
    return useQuery({
        queryKey: [
            'access:grantable-roles'
        ],
        enabled,
        retry: false,
        staleTime: 60000,
        queryFn: async ()=>{
            const resp = await authzClient.listGrantableRoles({});
            return resp.roles;
        }
    });
}
const SCOPE_TREE_KEY = 'access:scope-tree';
const SCOPE_ASSIGNMENTS_KEY = 'access:assignments-for-scope';
/** The tenant's scope tree, root-down (Wave 5 tree browser). */ export function useScopeTree(maxDepth = 0, options = {}) {
    const { enabled = true } = options;
    return useQuery({
        queryKey: [
            SCOPE_TREE_KEY,
            maxDepth
        ],
        enabled,
        staleTime: 60000,
        queryFn: async ()=>{
            const resp = await authzClient.adminGetScopeTree({
                maxDepth
            });
            return resp.nodes;
        }
    });
}
/** Reverse lookup: every assignment on one scope node (Wave 5). */ export function useAssignmentsForScopeNode(scopeNodeId, includeInactive = false) {
    return useQuery({
        queryKey: [
            SCOPE_ASSIGNMENTS_KEY,
            scopeNodeId,
            includeInactive
        ],
        enabled: !!scopeNodeId,
        queryFn: async ()=>{
            const resp = await authzClient.adminListAssignmentsForScopeNode({
                scopeNodeId,
                includeInactive
            });
            return resp.assignments;
        }
    });
}
/**
 * True for a not-yet-active grant: not revoked, and effective_start is a real
 * (non-zero) timestamp still in the future. Shared by the assignments list
 * and the scope-tree reverse lookup so "Scheduled" means the same thing in
 * both places (pangaea rejects delegation on these — see authz_designee.go's
 * `effective_start <= now()` covering check).
 */ export function isScheduledAssignment(a) {
    const isRevoked = !!a.revokedAt && Number(a.revokedAt.seconds) !== 0;
    if (isRevoked) return false;
    return !!a.effectiveStart && Number(a.effectiveStart.seconds) !== 0 && Number(a.effectiveStart.seconds) * 1000 > Date.now();
}
export function useCreateBundle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminCreateBundle(args),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    BUNDLES_KEY
                ]
            });
            toast.success('Custom role created');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create role');
        }
    });
}
export function useUpdateBundle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>// The editor always owns the full pack set — proto3 can't tell
            // "clear" from "not sent", so replacement is explicit.
            authzClient.adminUpdateBundle({
                ...args,
                replaceDataPacks: true
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    BUNDLES_KEY
                ]
            });
            toast.success('Role updated');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to update role');
        }
    });
}
export function useDeleteBundle() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (key)=>authzClient.adminDeleteBundle({
                key
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    BUNDLES_KEY
                ]
            });
            toast.success('Role deleted');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to delete role');
        }
    });
}
// ---------------------------------------------------------------------------
// Templates (finalization WS1/WS2). Versioned starting points, never
// grantable — applying snapshots a tenant instance; instances never
// auto-update when a new version publishes.
const ROLE_TEMPLATES_KEY = 'access:role-templates';
const GROUP_TEMPLATES_KEY = 'access:group-templates';
/** Platform + tenant-private role templates (the gallery + editor presets). */ export function useListRoleTemplates(status = '') {
    return useQuery({
        queryKey: [
            ROLE_TEMPLATES_KEY,
            status
        ],
        staleTime: 5 * 60000,
        queryFn: async ()=>{
            const resp = await authzClient.adminListRoleTemplates({
                status
            });
            return resp.templates;
        }
    });
}
/** Group templates with their pinned roles resolved (the team gallery). */ export function useListGroupTemplates(status = '') {
    return useQuery({
        queryKey: [
            GROUP_TEMPLATES_KEY,
            status
        ],
        staleTime: 5 * 60000,
        queryFn: async ()=>{
            const resp = await authzClient.adminListGroupTemplates({
                status
            });
            return resp.templates;
        }
    });
}
/** Verb/pack/view-mode delta between two versions of a template family. */ export function useTemplateDiff(familyId, fromVersion, toVersion) {
    return useQuery({
        queryKey: [
            'access:template-diff',
            familyId,
            fromVersion,
            toVersion
        ],
        enabled: !!familyId && fromVersion > 0 && toVersion > 0 && fromVersion !== toVersion,
        queryFn: async ()=>{
            const resp = await authzClient.adminGetTemplateDiff({
                familyId,
                fromVersion,
                toVersion
            });
            return resp.diff;
        }
    });
}
export function useApplyRoleTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminApplyRoleTemplate({
                templateId: args.templateId,
                overwriteExisting: args.overwriteExisting ?? false
            }),
        onSuccess: (resp, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    BUNDLES_KEY
                ]
            });
            toast.success(vars.overwriteExisting ? `Role "${resp.bundle?.displayName ?? ''}" updated to the new template version` : `Role "${resp.bundle?.displayName ?? ''}" is now active in your organization`);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to apply template');
        }
    });
}
/**
 * Create a team from a group template on a program (jargon-free like
 * useGrantOnProgram): ensure the program's scope node exists, then run the
 * transactional apply — roles materialize, the group gets scoped grants,
 * and it starts with ZERO members.
 */ export function useApplyGroupTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            let scopeNodeId = args.scopeNodeId ?? '';
            if (!scopeNodeId) {
                if (!args.programId) throw new Error('pick a program or an org unit');
                const node = await authzClient.adminCreateScopeNode({
                    kind: 'program',
                    targetObjectType: 'delivery.programs',
                    targetObjectId: args.programId,
                    displayName: args.programName ?? '',
                    sensitivity: 'standard'
                });
                scopeNodeId = node.scopeNode?.id ?? '';
                if (!scopeNodeId) throw new Error('could not resolve the program scope node');
            }
            return authzClient.adminApplyGroupTemplate({
                templateId: args.templateId,
                scopeNodeId,
                reason: args.reason ?? ''
            });
        },
        onSuccess: (resp)=>{
            qc.invalidateQueries({
                queryKey: [
                    BUNDLES_KEY
                ]
            });
            // GroupsPage list key (Groups/hooks.ts GROUPS_KEY).
            qc.invalidateQueries({
                queryKey: [
                    'admin:groups'
                ]
            });
            toast.success(`Team "${resp.group?.displayName ?? ''}" created with no members — add people to hand out its access`);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create team from template');
        }
    });
}
/** Snapshot a tenant role into a private template draft (publish to reuse). */ export function useSaveAsTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.adminSaveAsTemplate({
                bundleKey: args.bundleKey,
                displayName: args.displayName ?? '',
                description: args.description ?? ''
            }),
        onSuccess: (resp)=>{
            qc.invalidateQueries({
                queryKey: [
                    ROLE_TEMPLATES_KEY
                ]
            });
            toast.success(`Saved as private template v${resp.template?.version ?? 1} — publish it to make it appliable`);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to save as template');
        }
    });
}
export function usePublishTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (templateId)=>authzClient.adminPublishTemplate({
                templateId
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    ROLE_TEMPLATES_KEY
                ]
            });
            toast.success('Template published — it can now be applied');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to publish template');
        }
    });
}
// ---------------------------------------------------------------------------
// Access requests (finalization WS4). Any member may file/cancel their own;
// the server scopes the list — reviewers (access_grantors) see tenant-wide,
// everyone else sees only their own filings (response.reviewer says which).
const ACCESS_REQUESTS_KEY = 'access:requests';
export function useAccessRequests(status = '') {
    return useQuery({
        queryKey: [
            ACCESS_REQUESTS_KEY,
            status
        ],
        queryFn: async ()=>authzClient.listAccessRequests({
                status
            })
    });
}
export function classifyRequestAccessOutcome(resp) {
    if (resp.created !== false) return {
        kind: 'created',
        priorDenialAt: resp.priorDenialAt
    };
    if (resp.request?.status === 'pending') return {
        kind: 'pending'
    };
    return {
        kind: 'decided',
        status: resp.request?.status ?? 'unknown'
    };
}
// localCalendarDate reads a Timestamp back as the browser's own YMD, not
// UTC's. getFullYear/getMonth/getDate are local-time accessors —
// toISOString() would shift by the UTC offset and can roll a late-evening
// local date onto the neighboring UTC day, which would make a retry of the
// SAME submit mint a DIFFERENT idempotency key and defeat the whole point of
// having one.
function localCalendarDate(ts) {
    if (!ts) return '';
    const d = ts.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function useRequestAccess() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.requestAccess({
                targetObjectType: args.targetObjectType,
                targetObjectId: args.targetObjectId,
                intent: args.intent,
                message: args.message,
                originalOwnerUserId: args.originalOwnerUserId ?? '',
                delegateUserId: args.delegateUserId ?? '',
                effectiveStart: args.effectiveStart,
                effectiveEnd: args.effectiveEnd,
                // One stable key per (target, delegate, day) so an impatient
                // double-submit converges instead of erroring — the delegate is part
                // of the key because two different delegates for the same target on
                // the same day are two distinct requests, not a resubmit.
                //
                // Delegation is the one lane with a WINDOW (effectiveStart/End), and
                // the window belongs in the key in place of "today" — not alongside
                // it: two delegation filings for the same target/delegate on the
                // same day but DIFFERENT windows ("vacation mid-September AND
                // mid-November, both filed today") are different intents, not a
                // retry of one submit. A day-only key collided them onto one row
                // server-side — the idempotency-key lookup converged on window A
                // before 000114's window-scoped dedup ever got a chance to run.
                // Local calendar dates, not an ISO/UTC slice: the window came from
                // local date inputs (DelegateAccessRequestDialog), and a UTC shift
                // could roll one of those dates onto a neighboring day depending on
                // when a retry happens to land.
                idempotencyKey: args.intent === 'delegation' ? `${args.targetObjectType}:${args.targetObjectId}:${args.delegateUserId ?? ''}:` + `${localCalendarDate(args.effectiveStart)}:${localCalendarDate(args.effectiveEnd)}` : `${args.targetObjectType}:${args.targetObjectId}:${args.delegateUserId ?? ''}:${new Date().toISOString().slice(0, 10)}`
            }),
        onSuccess: (resp, vars)=>{
            qc.invalidateQueries({
                queryKey: [
                    ACCESS_REQUESTS_KEY
                ]
            });
            // A replay of an identical (owner, delegate, scope) delegation
            // converges on the existing pending row server-side (D12) instead of
            // filing anew - `created: false` - so it must not claim success. But
            // `created` has explicit proto3 presence (mixed-version rollout
            // hazard): a reply from an enies-lobby replica that predates this
            // field decodes with `created` ABSENT, not false, and that must still
            // read as a fresh success - only an explicit `false` means convergence.
            if (resp.created === false) return;
            toast.success(vars.intent === 'delegation' ? 'Delegation requested — the delegate receives it once a reviewer approves' : 'Access requested — a reviewer has been notified');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to request access');
        }
    });
}
export function useCancelAccessRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id)=>authzClient.cancelAccessRequest({
                id
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    ACCESS_REQUESTS_KEY
                ]
            });
            toast.success('Access request cancelled');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to cancel request');
        }
    });
}
export function useAccessRequest(id) {
    return useQuery({
        queryKey: [
            ACCESS_REQUESTS_KEY,
            'one',
            id
        ],
        enabled: !!id,
        queryFn: async ()=>{
            const resp = await authzClient.getAccessRequest({
                id
            });
            return resp.request;
        }
    });
}
export function useDecideAccessRequest() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>authzClient.decideAccessRequest({
                id: args.id,
                approve: args.approve,
                roleBundleKey: args.roleBundleKey ?? '',
                reason: args.reason ?? ''
            }),
        onSuccess: (resp)=>{
            qc.invalidateQueries({
                queryKey: [
                    ACCESS_REQUESTS_KEY
                ]
            });
            toast.success(resp.request?.status === 'approved' ? 'Access granted' : 'Request declined');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to decide request');
        }
    });
}
export function useArchiveTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (templateId)=>authzClient.adminArchiveTemplate({
                templateId
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    ROLE_TEMPLATES_KEY
                ]
            });
            toast.success('Template archived');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to archive template');
        }
    });
}
// ---------------------------------------------------------------------------
// Role names — the delivery-side vocabulary (`delivery.tenant_enum_values`
// with domain='role_type') that the /admin/permissions role-names panel joins
// to the authz bundle catalog. companyId is left empty on purpose: ryugu's
// resolveCompanyID takes the tenant from the gateway principal and only falls
// back to the body, which is the same convention the rest of /admin uses.
export const ROLE_NAMES_KEY = 'admin:role-names';
export const BUSINESS_UNITS_KEY = 'admin:business-units';
/** The tenant's role-name vocabulary, seeded rows included. */ export function useRoleNames() {
    return useQuery({
        queryKey: [
            ROLE_NAMES_KEY
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listTenantEnumValues({
                companyId: '',
                domain: 'role_type',
                includeSystem: true,
                // Empty = no BU filtering at all, so the panel shows every row —
                // company-wide names and BU-scoped ones together.
                businessUnitId: ''
            });
            return resp.values;
        }
    });
}
/** Business units, for the role-name scope picker. */ export function useBusinessUnitOptions() {
    return useQuery({
        queryKey: [
            BUSINESS_UNITS_KEY
        ],
        staleTime: 5 * 60000,
        queryFn: async ()=>{
            const resp = await deliveryClient.listBusinessUnits({
                companyId: '',
                sectorId: ''
            });
            return resp.businessUnits;
        }
    });
}
export function useUpsertRoleName() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.upsertTenantEnumValue({
                id: args.id,
                companyId: '',
                domain: 'role_type',
                code: args.code,
                displayLabel: args.displayLabel,
                description: args.description,
                sortOrder: args.sortOrder,
                isLeadership: args.isLeadership,
                roleBundleKey: args.roleBundleKey,
                businessUnitId: args.businessUnitId,
                selfService: args.selfService
            });
            return resp.value;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    ROLE_NAMES_KEY
                ]
            });
            // Same rows, second cache: the delivery surfaces (Registries → Program
            // Roles, the program Roles-tab picker) read the catalog under
            // ['tenantEnum', 'role_type', …] — an admin edit must reach them too.
            qc.invalidateQueries({
                queryKey: [
                    'tenantEnum',
                    'role_type'
                ]
            });
            toast.success('Role name saved');
        },
        onError: (err)=>{
            // pangaea owns the bundle-key / BU-ownership validation; its message is
            // the useful one, so it is surfaced verbatim rather than restated.
            toast.error(err instanceof Error ? err.message : 'Failed to save role name');
        }
    });
}
/** The row's roster self-service eligibility, read from its metadata. */ export function roleNameSelfService(metadataJson) {
    if (!metadataJson) return false;
    try {
        return Boolean(JSON.parse(metadataJson).self_service);
    } catch  {
        return false;
    }
}
